import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Button from 'components/field/Button';
import DateWithTime from 'components/field/DateWithTime';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { ORDER_ACTIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faRotate } from '@fortawesome/pro-regular-svg-icons';

const largeDefaultColSpan = {
	md: 12
};

const DeliverySlotForm = NiceModal.create(({ orderIDs, otoOrderIDs }) => {
	const { t } = useTranslation();
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();

	const [isLoading, setIsLoading] = useState(false);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					deliverySlotDate: yup
						.object()
						.shape({
							date: yup.date().nullable().required(t('dateIsMissing')),
							from: yup.string().nullable().required(t('fromHourIsMissing')),
							to: yup.string().nullable().required(t('toHourIsMissing'))
						})
						.required()
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			orderNumbers: orderIDs.join(', '),
			deliverySlotDate: {
				date: null,
				from: null,
				to: null
			}
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, errors }
	} = formMethods;

	const onSubmit = async (data) => {
		setIsLoading(true);

		try {
			const response = await axiosPrivate.post(ORDER_ACTIONS.CHANGE_DELIVERY_SLOT_BULK, {
				otoOrderIDs,
				...data
			});

			if (response.status === 200) {
				showSuccess({
					header: 'syncOrderSuccess.title',
					message: 'syncOrderSuccess.message'
				});
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
		setIsLoading(false);
		modal.remove();
	};

	return (
		<Modal maxWidth="medium" fullwidth className="modal-deliverySlot">
			<ModalTitle>
				<Typography variant="p1">deliverySlot</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={largeDefaultColSpan} label="orderNumbers">
						<StringInput name="orderNumbers" disabled={true} />
					</FormItem>

					<FormItem colSpan={largeDefaultColSpan} label="deliverySlotFrom" isLoading={isLoading}>
						<DateWithTime
							value={watch('deliverySlotDate')}
							errorMessage={
								errors.deliverySlotDate?.date?.message ||
								errors.deliverySlotDate?.from?.message ||
								errors.deliverySlotDate?.to?.message
							}
							setValue={(value) => {
								setValue('deliverySlotDate', value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button
					label="cancel"
					onClick={() => {
						modal.remove();
					}}
					variant="outline"
				/>
				<Button
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					icon={faRotate}
					label="save"
					variant="blue"
				/>
			</ModalActions>
		</Modal>
	);
});

export default DeliverySlotForm;
